/**
 * @file Small floating "keyboard shortcuts" affordance rendered beside the
 * "Drop or browse files" hint. Clicking it opens the cheat-sheet overlay — the
 * same overlay reachable via `?` / `⌘/` — giving the shortcut system a visible
 * discovery point. Purely a launcher: all state lives in `keyboardOverlayStore`.
 */

import { FC, useCallback, useMemo, useState } from 'react';
import { Tooltip, theme } from 'antd';
import { MacCommandOutlined } from '@ant-design/icons';
import { keyboardOverlayActions } from './keyboardOverlayStore';
import { keybindingRegistry } from './keybindingRegistry';
import { formatChord } from './formatChord';
import { getPlatform } from './platform';
import { ariaKeyshortcutsFor } from './ariaKeyshortcutsFor';

export const KeyboardShortcutHint: FC = () => {
  const { token } = theme.useToken();
  const [hovered, setHovered] = useState(false);

  const shortcut = useMemo(() => {
    const chord = keybindingRegistry.byId('global.cheatSheet')?.chords[0];
    return chord ? formatChord(chord, getPlatform()) : '';
  }, []);

  const handleClick = useCallback(() => {
    keyboardOverlayActions.toggleCheatSheet();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <Tooltip title={shortcut ? `Keyboard shortcuts (${shortcut})` : 'Keyboard shortcuts'}>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: token.controlHeightSM,
          height: token.controlHeightSM,
          borderRadius: 20,
          cursor: 'pointer',
          transition: 'filter 0.2s ease',
          filter: hovered ? 'brightness(0.95)' : undefined,
          outline: 'none',
          backgroundColor: token.colorFillQuaternary,
        }}
        aria-label="Keyboard shortcuts"
        aria-keyshortcuts={ariaKeyshortcutsFor('global.cheatSheet')}
      >
        <MacCommandOutlined style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }} />
      </div>
    </Tooltip>
  );
};
